import styled, { keyframes } from 'styled-components';
import {
  Box,
  Paper,
  Button,
  TextField,
  Typography,
  InputLabel,
} from '@mui/material';

/* ─── shared keyframes ─── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;



/* ─── layout ─── */
export const DashboardContainer = styled(Box)`
  display: flex;
  height: 100%;

  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

export const SidebarContainer = styled(Box)`
  width: 240px;
  background-color: #fff;
  border-right: 1px solid #e0e0e0;
  padding: 16px;

  @media (max-width: 960px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
    padding: 12px 16px;
  }
`;

export const ContentWrapper = styled(Box)`
  flex: 1;
  padding: 24px;
  min-height: calc(100vh - 120px);

  @media (max-width: 960px) {
    padding: 16px;
    width: 100%;
    min-height: auto;
  }
`;

/* ─── header ─── */
export const BusinessHeaderBox = styled(Box)`
  background: linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%);
  padding: 36px 24px;
  border-radius: 16px;
  margin-bottom: 32px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  color: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  @media (max-width: 960px) {
    flex-direction: column;
    padding: 24px 20px;
    margin-bottom: 24px;
    gap: 20px;
  }
`;

export const BusinessHeading = styled(Typography)`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 8px;

  @media (max-width: 960px) {
    font-size: 2rem;
  }
  @media (max-width: 600px) {
    font-size: 1.75rem;
  }
`;

export const WelcomeText = styled(Typography)`
  font-size: 1.25rem;
  color: #e0f7fa;
`;

export const StorefrontButton = styled(Button)`
  font-weight: 600;
  text-transform: none;
  border-radius: 8px;
  transition: all 0.3s ease;
  background-color: #0d4efe;
  color: white;
  margin-top: 16px;

  &:hover {
    background-color: #01ebb2;
  }
`;

/* ─── photo ─── */
export const PhotoUploadContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  @media (max-width: 960px) {
    align-items: flex-start;
  }
`;

export const PhotoContainer = styled(Box)`
  width: 160px;
  height: 160px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  border: 2px solid #e0e0e0;
`;

export const PhotoPlaceholder = styled(Box)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  color: #666;
`;

export const BrandButton = styled(Button)`
  font-weight: 600;
  text-transform: none;
  border-radius: 8px;
  transition: all 0.3s ease;
  background-color: #0d4efe;
  color: white;

  &:hover {
    background-color: #01ebb2;
  }
`;

export const PhotoUploadButton = styled(BrandButton)`
  min-width: 120px;
`;

export const PhotoError = styled(Typography)`
  text-align: center;
`;

/* ─── info paper (business info section) ─── */
export const InfoPaper = styled(Paper)`
  padding: 32px;
  margin-top: 16px;
  position: relative;
  border-radius: 16px !important;
  border: 1px solid #e4e8f1 !important;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06) !important;
`;

export const BusinessInfoTitle = styled(Typography)`
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #1a1a2e;
  margin-bottom: 4px;
`;

export const FormNotice = styled(Box)`
  color: #5a6578;
  font-weight: 500;
  font-size: 0.92rem;
  line-height: 1.6;
  margin-bottom: 28px;
  padding: 12px 16px;
  background: #f0f4ff;
  border-left: 4px solid #0d4efe;
  border-radius: 0 10px 10px 0;
`;

export const SaveButton = styled(Button)`
  margin-top: 28px;
  margin-right: 12px;
  background: linear-gradient(135deg, #01ebb2, #0bbfa0) !important;
  color: #fff !important;
  font-weight: 700;
  text-transform: none;
  border-radius: 10px;
  padding: 10px 28px;
  font-size: 0.95rem;
  box-shadow: 0 2px 8px rgba(1, 235, 178, 0.3) !important;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(1, 235, 178, 0.45) !important;
    transform: translateY(-1px);
  }
`;

export const CancelButton = styled(Button)`
  margin-top: 28px;
  font-weight: 600;
  text-transform: none;
  border-radius: 10px;
  padding: 10px 28px;
  font-size: 0.95rem;
  color: #5a6578 !important;
  border-color: #d0d5e0 !important;

  &:hover {
    background: #f5f6f8 !important;
    border-color: #b0b8c8 !important;
  }
`;

export const StyledTextField = styled(TextField)`
  width: 100%;

  .MuiOutlinedInput-root {
    border-radius: 10px;
    background: #fafbfd;
    transition: background 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      background: #fff;
    }

    &.Mui-focused {
      background: #fff;
      box-shadow: 0 0 0 3px rgba(13, 78, 254, 0.10);
    }
  }
`;

export const InfoBlock = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  margin-bottom: 4px;
  border-radius: 12px;
  transition: background 0.15s ease;

  &:hover {
    background: #f8f9fc;
  }
`;

export const InfoBlockIconWrap = styled(Box)`
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ bg }) => bg || '#f0f4ff'};
  margin-top: 2px;

  svg {
    font-size: 1.15rem;
    color: ${({ iconColor }) => iconColor || '#0d4efe'};
  }
`;

export const InfoBlockText = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const Label = styled(Typography)`
  font-weight: 700;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8893a4;
  margin-bottom: 0;
`;

export const Value = styled(Typography)`
  color: #1a1a2e;
  font-size: 1rem;
  font-weight: 500;
`;

export const InfoChip = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eef8ff;
  color: #0891b2;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  margin-left: 8px;
  vertical-align: middle;
  letter-spacing: 0.02em;

  svg {
    font-size: 0.85rem;
  }

  &:hover {
    background: #d8f0fa;
    color: #0e7490;
    transform: translateY(-1px);
  }
`;

export const PopoverContent = styled(Box)`
  padding: 20px 24px;
  max-width: 320px;
  border-radius: 12px;
`;

export const PopoverTitle = styled(Typography)`
  font-size: 0.92rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const PopoverBody = styled(Typography)`
  font-size: 0.85rem;
  font-weight: 450;
  color: #5a6578;
  line-height: 1.55;
`;

export const EditButton = styled(Button)`
  position: absolute;
  top: 20px;
  right: 20px;
  text-transform: none;
  font-weight: 600;
  font-size: 0.85rem;
  border-radius: 9px;
  padding: 6px 16px;
  color: #0d4efe !important;
  border: 1.5px solid #c9d4f0 !important;
  background: #f5f7ff !important;
  gap: 6px;
  min-width: auto;
  transition: all 0.18s ease;

  svg {
    font-size: 1.05rem;
  }

  &:hover {
    background: #eaefff !important;
    border-color: #0d4efe !important;
  }
`;

export const LabeledInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const LabelWithIcon = styled(InputLabel)`
  display: flex;
  align-items: center;
  font-weight: 600;
  gap: 4px;
  font-size: 0.95rem;

  svg {
    font-size: 1rem;
    cursor: pointer;
  }
`;

/* ═══════════════════════════════════════════
   ANALYTICS  –  enhanced visual design
   ═══════════════════════════════════════════ */

export const AnalyticsSection = styled(Box)`
  margin-bottom: 32px;
  animation: ${fadeInUp} 0.5s ease both;
`;

export const AnalyticsHeader = styled(Box)`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 6px;
`;

export const AnalyticsTitle = styled(Typography)`
  font-size: 1.85rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #0d4efe 0%, #01ebb2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const AnalyticsPeriodBadge = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e8edff;
  color: #2d46b9;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
  border: 1px solid #d0d9f5;
`;

/** The KPI row container */
export const KpiRow = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 28px;
`;

/** Individual KPI card */
export const KpiCard = styled(Paper)`
  position: relative;
  overflow: hidden;
  border-radius: 16px !important;
  padding: 28px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  animation: ${fadeInUp} 0.45s ease both;
  animation-delay: ${({ delay }) => delay || '0s'};
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  cursor: default;
  border: 1px solid #e4e8f1 !important;
  background: #ffffff !important;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06) !important;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 32px rgba(0,0,0,0.13) !important;
  }

  /* decorative accent bar — thicker for visibility */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: ${({ accent }) => accent || 'linear-gradient(90deg, #01ebb2, #0d4efe)'};
  }
`;

export const KpiIconWrap = styled(Box)`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ bg }) => bg || '#f0f4ff'};
  margin-bottom: 8px;

  svg {
    font-size: 1.35rem;
    color: ${({ iconColor }) => iconColor || '#4361ee'};
  }
`;

export const KpiValue = styled(Typography)`
  font-size: 2.25rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: #1a1a2e;
`;

export const KpiLabel = styled(Typography)`
  font-size: 0.82rem;
  font-weight: 700;
  color: #5a6578;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

/** Service breakdown card */
export const ServiceBreakdownCard = styled(Paper)`
  border-radius: 16px !important;
  padding: 28px;
  border: 1px solid #e4e8f1 !important;
  background: #ffffff !important;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06) !important;
  animation: ${fadeInUp} 0.5s ease both;
  animation-delay: 0.15s;
`;

export const ServiceBreakdownTitle = styled(Typography)`
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

/** Each service group row */
export const ServiceGroupRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 10px;
  background: ${({ isParent }) => (isParent ? '#edf1fb' : 'transparent')};
  margin-bottom: ${({ isParent }) => (isParent ? '2px' : '0')};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ isParent }) => (isParent ? '#e2e8f8' : '#f5f6f8')};
  }
`;

export const ServiceName = styled(Typography)`
  font-size: ${({ isChild }) => (isChild ? '0.88rem' : '0.95rem')};
  font-weight: ${({ isChild }) => (isChild ? 500 : 700)};
  color: ${({ isChild }) => (isChild ? '#5a6978' : '#1a1a2e')};
  padding-left: ${({ isChild }) => (isChild ? '28px' : '0')};
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ServiceCount = styled(Typography)`
  font-size: ${({ isChild }) => (isChild ? '0.88rem' : '1.1rem')};
  font-weight: ${({ isChild }) => (isChild ? 600 : 800)};
  color: ${({ isChild }) => (isChild ? '#5a6978' : '#1a1a2e')};
`;

/** Bar chart mini-visual behind each service row */
export const ServiceBar = styled(Box)`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({ pct }) => pct || '0%'};
  background: linear-gradient(90deg, rgba(1, 235, 178, 0.10), rgba(13, 78, 254, 0.08));
  border-radius: 10px;
  transition: width 0.6s ease;
`;

export const ServiceGroupWrapper = styled(Box)`
  position: relative;
`;

/* ─── sparkline-style mini chart (optional decoration) ─── */
export const SparklineWrap = styled(Box)`
  height: 40px;
  margin-top: 8px;
  opacity: 0.55;
`;

/* ─── analytics empty / error states ─── */
export const AnalyticsEmptyState = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: #7b8794;
  gap: 8px;
`;